import type { ScanResult } from "./api";

/// Retorno sensorial da bipagem.
///
/// O operador está de pé, com o celular numa mão e o pacote na outra, e nem
/// sempre olhando a tela. Som e vibração precisam distinguir os desfechos
/// sozinhos — por isso cada um tem timbre, duração e padrão de vibração
/// próprios, em vez de um bipe único para tudo.
///
/// Os sons são gerados por WebAudio, não são arquivos: um bipe sintetizado
/// toca imediatamente, sem download nem cache, e funciona offline desde a
/// primeira leitura.

let ctx: AudioContext | null = null;

/// O navegador só libera áudio depois de um gesto do usuário. Chamado no toque
/// que abre a câmera, para que o primeiro bipe já saia.
export function armAudio(): void {
  if (!ctx) {
    const Ctor = window.AudioContext ?? (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (Ctor) ctx = new Ctor();
  }
  void ctx?.resume();
}

function tone(freq: number, durationMs: number, startMs = 0, volume = 0.22): void {
  if (!ctx) return;

  const start = ctx.currentTime + startMs / 1000;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();

  osc.type = "square";
  osc.frequency.value = freq;

  // Ataque e queda curtos evitam o "clique" de ligar e desligar a onda seco.
  gain.gain.setValueAtTime(0, start);
  gain.gain.linearRampToValueAtTime(volume, start + 0.008);
  gain.gain.setValueAtTime(volume, start + durationMs / 1000 - 0.02);
  gain.gain.linearRampToValueAtTime(0, start + durationMs / 1000);

  osc.connect(gain).connect(ctx.destination);
  osc.start(start);
  osc.stop(start + durationMs / 1000 + 0.02);
}

function vibrate(pattern: number | number[]): void {
  if ("vibrate" in navigator) navigator.vibrate(pattern);
}

export function playFeedback(result: ScanResult): void {
  armAudio();

  switch (result) {
    case "OK":
      // Agudo, curto, único: o som de "pode seguir".
      tone(1180, 90);
      vibrate(45);
      break;

    case "DUPLICATE":
      // Dois tons médios repetidos — pede atenção sem soar como erro, porque
      // duplicata não é falha do operador.
      tone(760, 110);
      tone(760, 110, 160);
      vibrate([60, 70, 60]);
      break;

    case "PENDING_ACCOUNT":
      // Sobe: falta um passo, e o passo é na tela.
      tone(680, 90);
      tone(1000, 130, 110);
      vibrate([40, 60, 90]);
      break;

    case "UNIDENTIFIED":
      // Grave e longo: o único desfecho em que nada foi registrado.
      tone(240, 300);
      vibrate([120, 80, 200]);
      break;
  }
}

export const FEEDBACK_LABEL: Record<ScanResult, string> = {
  OK: "Bipado",
  DUPLICATE: "Já bipado",
  PENDING_ACCOUNT: "Escolha a conta",
  UNIDENTIFIED: "Pacote não encontrado",
};
